import React, { useState, useEffect } from 'react';
import { getCompanies, createCompany, createCompanyAdmin } from '../../lib/api';
import { toast } from 'sonner';
import { Building2, Plus, UserPlus, FileText, Settings, ShieldAlert } from 'lucide-react';

export default function SuperadminDashboard() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  // Forms
  const [companyName, setCompanyName] = useState('');
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminName, setAdminName] = useState('');

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const data = await getCompanies();
      setCompanies(data);
    } catch (err) {
      toast.error('Fehler beim Laden der Unternehmen');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      const newComp = await createCompany({ name: companyName });
      toast.success('Unternehmen erfolgreich erstellt');
      setShowCompanyModal(false);
      setCompanyName('');
      loadCompanies();
      
      // Auto-open admin creation for convenience
      setSelectedCompanyId(newComp.id);
      setShowAdminModal(true);
    } catch (err) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      await createCompanyAdmin(selectedCompanyId, {
        username: adminUsername,
        password: adminPassword,
        name: adminName,
        role: "admin",
        permissions: ["all"],
        pin: ""
      });
      toast.success('Admin-Benutzer erfolgreich erstellt');
      setShowAdminModal(false);
      setAdminUsername('');
      setAdminPassword('');
      setAdminName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen des Admins');
    }
  };

  if (loading) return <div>Laden...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-white p-6 rounded-xl shadow-sm border border-slate-100">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-rose-500" />
            Superadmin Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Plattformweite SaaS Mandantenverwaltung</p>
        </div>
        <button
          onClick={() => setShowCompanyModal(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          Neues Unternehmen
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companies.map((comp) => (
          <div key={comp.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-lg">
                  <Building2 className="w-6 h-6" />
                </div>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${comp.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {comp.is_active ? 'Aktiv' : 'Inaktiv'}
                </span>
              </div>
              
              <h3 className="text-xl font-bold text-slate-800 mb-1">{comp.name}</h3>
              <p className="text-slate-500 text-sm mb-2 font-mono text-xs" title="Company ID">{comp.id}</p>
              
              <div className="text-sm text-slate-500 mb-6 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span>
                  Erstellt am: {new Date(comp.created_at).toLocaleDateString('de-DE', {
                    day: '2-digit', month: '2-digit', year: 'numeric'
                  })}
                </span>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => { setSelectedCompanyId(comp.id); setShowAdminModal(true); }}
                  className="flex-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                  title="Admin-Benutzer anlegen"
                >
                  <UserPlus className="w-4 h-4" />
                  Admin
                </button>
                <button
                  onClick={() => toast.info('Einstellungen bearbeiten (Demnächst)')}
                  className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg transition-colors"
                  title="Einstellungen"
                >
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showCompanyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <h2 className="text-xl font-bold text-slate-800">Neues Unternehmen (Mandant)</h2>
            </div>
            <form onSubmit={handleCreateCompany} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Unternehmensname</label>
                <input
                  type="text"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="z.B. Pizzeria Napoli"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setShowCompanyModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Abbrechen</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Erstellen</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAdminModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <h2 className="text-xl font-bold text-slate-800">Administrator erstellen</h2>
              <p className="text-sm text-slate-500 mt-1">Dieser Benutzer verwaltet das Unternehmen (Menu, Räume, Tische).</p>
            </div>
            <form onSubmit={handleCreateAdmin} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Vollständiger Name</label>
                <input type="text" required value={adminName} onChange={(e) => setAdminName(e.target.value)} className="w-full px-4 py-2 border border-slate-200 rounded-lg" placeholder="Max Mustermann" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Benutzername (Login)</label>
                <input type="text" required value={adminUsername} onChange={(e) => setAdminUsername(e.target.value)} className="w-full px-4 py-2 border border-slate-200 rounded-lg" placeholder="admin" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Passwort</label>
                <input type="password" required value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} className="w-full px-4 py-2 border border-slate-200 rounded-lg" />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setShowAdminModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Abbrechen</button>
                <button type="submit" className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700">Admin anlegen</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
